"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AlertCircle, AlertTriangle, FileSpreadsheet, History, PackageX, Plus, Search, ShoppingCart, X } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import MultiSelectFilterCombobox from "./MultiSelectFilterCombobox"
import InventoryManagementDialogs from "./InventoryManagementDialogs"
import InventoryManagementTable from "./InventoryManagementTable"
import BulkIssueDialog from "./BulkIssueDialog"
import BulkPurchaseDialog from "./BulkPurchaseDialog"
import useInventoryManagement from "./hooks/useInventoryManagement"
import { fetchCustomers } from "../../../services/ledgerService"

const InventoryManagement = () => {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState([])
  const [showBulkIssue, setShowBulkIssue] = useState(false)
  const [showBulkPurchase, setShowBulkPurchase] = useState(false)

  useEffect(() => {
    fetchCustomers().then((d) => setCustomers(d.data || [])).catch(() => {})
  }, [])

  const {
    uiConfigLoading,
    canEdit,
    canHistory,
    showAddItem,
    showPurchase,
    showIssue,
    inventory,
    reorderAlerts,
    nonMovingItems,
    categories,
    units,
    sizes,
    brands,
    colors,
    vendors,
    isColorEnabled,
    isSubCategoryEnabled,
    subCategoryOptions,
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    nonMovingDays,
    setNonMovingDays,
    filterOptions,
    setFilterOptions,
    clearFilters,
    loading,
    error,
    message,
    setMessage,
    sortColumn,
    sortDirection,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalInventory,
    tableData,
    paginatedData,
    currentTotal,
    lowStockCount,
    hasBrand,
    hasSize,
    hasDescription,
    brandOptions,
    selectedItem,
    setSelectedItem,
    showAddForm,
    setShowAddForm,
    showEditForm,
    setShowEditForm,
    showPurchaseForm,
    setShowPurchaseForm,
    showIssueForm,
    setShowIssueForm,
    showHoldForm,
    setShowHoldForm,
    showUnholdForm,
    setShowUnholdForm,
    showAdjustForm,
    setShowAdjustForm,
    newItem,
    setNewItem,
    editItem,
    setEditItem,
    newPurchase,
    setNewPurchase,
    newIssue,
    setNewIssue,
    issueLedger,
    setIssueLedger,
    issueLedgerData,
    setIssueLedgerData,
    holdQuantity,
    setHoldQuantity,
    unholdQuantity,
    setUnholdQuantity,
    newAdjustment,
    setNewAdjustment,
    fetchInventory,
    handleInventorySort,
    openAddForm,
    openPurchaseForm,
    openIssueForm,
    handleAddItem,
    handleEditItem,
    handleAddPurchase,
    handleIssueStock,
    handleAdjustStock,
    handleUnholdStock,
    handleHoldStock,
  } = useInventoryManagement()

  const handleRowClick = (item) => {
    navigate(`/dashboard/inventory/${item.inventory_id}`)
  }

  const hasActiveFilters =
    filterOptions.category.length > 0 ||
    filterOptions.sub_category.length > 0 ||
    filterOptions.status.length > 0 ||
    filterOptions.brand.length > 0 ||
    filterOptions.size.length > 0 ||
    filterOptions.color.length > 0 ||
    filterOptions.activeStatus.length !== 1 ||
    filterOptions.activeStatus[0] !== "active"

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Available":
        return "bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium"
      case "Low Stock":
        return "bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium"
      case "Out of Stock":
        return "bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium"
      default:
        return "bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium"
    }
  }

  if (error && !inventory.length) {
    return (
      <div className="p-6">
        <Alert variant="destructive" className="bg-red-50 text-red-800 border-red-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 mr-2" />
          <AlertDescription className="font-medium">{error}</AlertDescription>
        </Alert>
        <div className="mt-4 flex justify-center">
          <Button onClick={fetchInventory} className="bg-[#0263cc] hover:bg-[#0263cc]/90">
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mx-auto space-y-4">
      {uiConfigLoading || (loading && !inventory.length) ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border border-gray-200 shadow-sm animate-pulse">
              <CardContent className="p-4">
                <div className="h-3 bg-gray-200 rounded w-20 mb-3" />
                <div className="h-8 bg-gray-200 rounded w-14" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border border-[#0263cc]/10 shadow-sm">
            <CardContent className="p-4">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Items</div>
              <div className="text-2xl font-bold text-[#0263cc] mt-1">{activeTab === "all" ? totalInventory : inventory.length}</div>
            </CardContent>
          </Card>
          <Card className="border border-amber-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Low Stock</div>
              <div className="text-2xl font-bold text-amber-600 mt-1">{lowStockCount}</div>
            </CardContent>
          </Card>
          <Card className="border border-orange-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Reorder Alerts</div>
              <div className="text-2xl font-bold text-orange-600 mt-1">{reorderAlerts.length}</div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Non-Moving</div>
              <div className="text-2xl font-bold text-slate-600 mt-1">{nonMovingItems.length}</div>
            </CardContent>
          </Card>
        </div>
      )}
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <CardTitle className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col gap-3 w-full mb-4">
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                {showAddItem && (
                  <Button
                    onClick={() => navigate("/dashboard/inventory/add")}
                    className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white rounded-lg flex items-center gap-2 w-full sm:w-auto whitespace-nowrap"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">Add Item</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                )}

                {canHistory && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/dashboard/inventory/all-transaction")}
                    className="border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/5 rounded-lg flex items-center gap-1.5 px-2 sm:px-3 text-sm w-full sm:w-auto whitespace-nowrap"
                  >
                    <History className="h-4 w-4" />
                    <span className="hidden sm:inline">All transactions</span>
                    <span className="sm:hidden">History</span>
                  </Button>
                )}

                {canEdit && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate("/dashboard/inventory/settings/import-export")}
                      className="border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/5 rounded-lg flex items-center gap-1.5 px-2 sm:px-3 text-sm w-full sm:w-auto whitespace-nowrap"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      <span className="hidden sm:inline">Import/Export</span>
                      <span className="sm:hidden">I/E</span>
                    </Button>
                  </>
                )}

                {showIssue && (
                  <Button
                    onClick={() => setShowBulkIssue(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 w-full sm:w-auto whitespace-nowrap"
                  >
                    <PackageX className="h-4 w-4" />
                    <span className="hidden sm:inline">Bulk Issue</span>
                    <span className="sm:hidden">Issue</span>
                  </Button>
                )}

                {showPurchase && (
                  <Button
                    onClick={() => setShowBulkPurchase(true)}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 w-full sm:w-auto whitespace-nowrap"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    <span className="hidden sm:inline">Bulk Purchase</span>
                    <span className="sm:hidden">Purchase</span>
                  </Button>
                )}
              </div>
              <div className="relative w-full xl:w-96">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by item, brand, category, vendor, size, description or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full"
                />
              </div>
            </div>
          </CardTitle>

          <div className="flex flex-col gap-3 mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex rounded-lg border border-[#0263cc]/20 overflow-hidden">
              <button
                type="button"
                onClick={() => setActiveTab("all")}
                className={`px-4 py-2 text-sm font-medium ${activeTab === "all" ? "bg-[#0263cc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              >
                All Items
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("reorder")}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${activeTab === "reorder" ? "bg-[#0263cc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              >
                <AlertCircle className="h-4 w-4" />
                Reorder Alerts
                {reorderAlerts.length > 0 && (
                  <span className={`ml-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${activeTab === "reorder" ? "bg-white/20" : "bg-orange-100 text-orange-700"}`}>
                    {reorderAlerts.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("nonmoving")}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 ${activeTab === "nonmoving" ? "bg-[#0263cc] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              >
                <PackageX className="h-4 w-4" />
                Non-Moving
                {nonMovingItems.length > 0 && (
                  <span className={`ml-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${activeTab === "nonmoving" ? "bg-white/20" : "bg-slate-200 text-slate-700"}`}>
                    {nonMovingItems.length}
                  </span>
                )}
              </button>
            </div>
            {activeTab === "nonmoving" && (
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Older than</Label>
                <Select
                  value={nonMovingDays === 30 || nonMovingDays === 90 || nonMovingDays === 180 ? String(nonMovingDays) : "custom"}
                  onValueChange={(v) => setNonMovingDays(v === "custom" ? nonMovingDays : Number(v))}
                >
                  <SelectTrigger className="w-[120px] border-[#0263cc]/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {(nonMovingDays !== 30 && nonMovingDays !== 90 && nonMovingDays !== 180) && (
                  <Input
                    type="number"
                    min={1}
                    value={nonMovingDays}
                    onChange={(e) => setNonMovingDays(Number(e.target.value) || 90)}
                    className="w-20"
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
            <MultiSelectFilterCombobox
              placeholder="Category"
              allLabel="All Categories"
              value={filterOptions.category}
              onValueChange={(value) => setFilterOptions({ ...filterOptions, category: value })}
              options={categories.map((category) => ({ key: category._id, value: category.name, label: category.name }))}
            />
            {isSubCategoryEnabled && (
              <MultiSelectFilterCombobox
                placeholder="Sub-category"
                allLabel="All Sub-categories"
                value={filterOptions.sub_category}
                onValueChange={(value) => setFilterOptions({ ...filterOptions, sub_category: value })}
                options={subCategoryOptions.map((subCategory) => ({ key: subCategory._id, value: subCategory.name, label: subCategory.name }))}
              />
            )}
            <MultiSelectFilterCombobox
              placeholder="Status"
              allLabel="All Statuses"
              value={filterOptions.status}
              onValueChange={(value) => setFilterOptions({ ...filterOptions, status: value })}
              options={[
                { key: "Available", value: "Available", label: "Available" },
                { key: "Low Stock", value: "Low Stock", label: "Low Stock" },
                { key: "Out of Stock", value: "Out of Stock", label: "Out of Stock" },
              ]}
            />
            <MultiSelectFilterCombobox
              placeholder="Active/Inactive"
              allLabel="Active & Inactive"
              value={filterOptions.activeStatus}
              onValueChange={(value) => setFilterOptions({ ...filterOptions, activeStatus: value })}
              options={[
                { key: "active", value: "active", label: "Active" },
                { key: "inactive", value: "inactive", label: "Inactive" },
              ]}
            />
            {hasBrand && (
              <MultiSelectFilterCombobox
                placeholder="Brand"
                allLabel="All Brands"
                value={filterOptions.brand}
                onValueChange={(value) => setFilterOptions({ ...filterOptions, brand: value })}
                options={brandOptions.map((brand) => ({ key: brand, value: brand, label: brand }))}
              />
            )}
            {sizes.length > 0 && (
              <MultiSelectFilterCombobox
                placeholder="Size"
                allLabel="All Sizes"
                value={filterOptions.size}
                onValueChange={(value) => setFilterOptions({ ...filterOptions, size: value })}
                options={sizes.map((size) => ({ key: size._id, value: size.name, label: size.name }))}
              />
            )}
            {isColorEnabled && colors.length > 0 && (
              <MultiSelectFilterCombobox
                placeholder="Color"
                allLabel="All Colors"
                value={filterOptions.color}
                onValueChange={(value) => setFilterOptions({ ...filterOptions, color: value })}
                options={colors.map((color) => ({ key: color._id, value: color.name, label: color.name }))}
              />
            )}
            {hasActiveFilters && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filter
              </Button>
            )}
          </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {message.content && (
            <Alert
              variant={message.type === "error" ? "destructive" : "default"}
              className={`m-4 rounded-lg ${
                message.type === "success"
                  ? "bg-green-50 text-green-800 border-green-200"
                  : message.type === "error"
                    ? "bg-red-50 text-red-800 border-red-200"
                    : "bg-blue-50 text-blue-800 border-blue-200"
              }`}
            >
              <AlertDescription className="font-medium">{message.content}</AlertDescription>
            </Alert>
          )}
          <InventoryManagementTable
            activeTab={activeTab}
            tableData={tableData}
            paginatedData={paginatedData}
            loading={loading}
            searchTerm={searchTerm}
            showAddItem={showAddItem}
            openAddForm={openAddForm}
            showPurchase={showPurchase}
            showIssue={showIssue}
            openPurchaseForm={openPurchaseForm}
            openIssueForm={openIssueForm}
            handleRowClick={handleRowClick}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleInventorySort={handleInventorySort}
            getStatusBadgeClass={getStatusBadgeClass}
            currentPage={currentPage}
            currentTotal={currentTotal}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
            setItemsPerPage={setItemsPerPage}
            isColorEnabled={isColorEnabled}
            isSubCategoryEnabled={isSubCategoryEnabled}
          />
        </CardContent>
      </Card>

      <InventoryManagementDialogs
        loading={loading}
        setMessage={setMessage}
        inventory={inventory}
        categories={categories}
        vendors={vendors}
        units={units}
        sizes={sizes}
        brands={brands}
        colors={colors}
        isColorEnabled={isColorEnabled}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        showPurchaseForm={showPurchaseForm}
        setShowPurchaseForm={setShowPurchaseForm}
        newPurchase={newPurchase}
        setNewPurchase={setNewPurchase}
        handleAddPurchase={handleAddPurchase}
        showIssueForm={showIssueForm}
        setShowIssueForm={setShowIssueForm}
        newIssue={newIssue}
        setNewIssue={setNewIssue}
        issueLedger={issueLedger}
        setIssueLedger={setIssueLedger}
        issueLedgerData={issueLedgerData}
        setIssueLedgerData={setIssueLedgerData}
        customers={customers}
        handleIssueStock={handleIssueStock}
        showAdjustForm={showAdjustForm}
        setShowAdjustForm={setShowAdjustForm}
        newAdjustment={newAdjustment}
        setNewAdjustment={setNewAdjustment}
        handleAdjustStock={handleAdjustStock}
        showUnholdForm={showUnholdForm}
        setShowUnholdForm={setShowUnholdForm}
        unholdQuantity={unholdQuantity}
        setUnholdQuantity={setUnholdQuantity}
        handleUnholdStock={handleUnholdStock}
        showHoldForm={showHoldForm}
        setShowHoldForm={setShowHoldForm}
        holdQuantity={holdQuantity}
        setHoldQuantity={setHoldQuantity}
        handleHoldStock={handleHoldStock}
      />

      <BulkIssueDialog
        open={showBulkIssue}
        onOpenChange={setShowBulkIssue}
        onSuccess={fetchInventory}
      />
      <BulkPurchaseDialog
        open={showBulkPurchase}
        onOpenChange={setShowBulkPurchase}
        onSuccess={fetchInventory}
      />

    </div>
  )
}

export default InventoryManagement
